/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, WritableSignal, signal } from '@angular/core';
import { BaseService } from './base.service';
import { ApiResp, FilePayload } from '@app/types/global.type';
import { Observable, catchError, map, of } from 'rxjs';
import { ReturnUpload, UploadHistory } from '@app/types/returns.type';
import { FileResponse, RejectionReasonList } from '@app/types/gstr1.type';
import { ReturnStatus, GSTRStatus } from '@app/types/gstr3b.type';
import {
  accessControlList,
  DEFAULT_EMAIL_CONTENT_HTML,
} from '@app/pages/private/gstr1/gstr1-view-uploads/constant';
import {
  DOCUMENT_DETAILS_DESC,
  STATE_CODES,
  SUMMARY_MAIN_HEADER,
  SUMMARY_MAIN_HEADER_BEFORE_2024,
  SUMMARY_MAIN_HEADER_GSTR6,
  ammendSecSummaryKeys as amendSecSummaryKeys,
  b2bSecSummaryKeys,
  table14SupEcomKeys,
  table15EcomKeys,
} from '@app/constant/gstr1.const';

@Injectable({
  providedIn: 'root',
})
export class CommonService extends BaseService {
  gstr3BStatus: WritableSignal<ReturnStatus | null> = signal<ReturnStatus | null>(null);
  gstr1Status: WritableSignal<ReturnStatus | null> = signal<ReturnStatus | null>(null);
  gstr1AStatus: WritableSignal<ReturnStatus | null> = signal<ReturnStatus | null>(null);
  gstr6Status: WritableSignal<ReturnStatus | null> = signal<ReturnStatus | null>(null);
  mainHeaderBefore2024 = SUMMARY_MAIN_HEADER_BEFORE_2024;
  mainHeaderGSTR6 = SUMMARY_MAIN_HEADER_GSTR6;
  mainHeader = SUMMARY_MAIN_HEADER;
  b2bSecSummaryKeys = b2bSecSummaryKeys;
  gstrStatus: WritableSignal<GSTRStatus | null> = signal<GSTRStatus | null>(null);
  isGarnetAdmin: WritableSignal<boolean | null> = signal<boolean | null>(null);
  statusOfGstins: WritableSignal<any | null> = signal<any | null>(null);

  STATE_CODES: any = STATE_CODES;

  updateGstrStatus(code: string) {
    const newStatus: GSTRStatus = {
      code: code,
      status: accessControlList[code].status,
    };
    this.gstrStatus.set(newStatus);
    return newStatus;
  }

  uploadFileGSTR1(payload: any, formData: FormData): Observable<ReturnUpload | null> {
    const params = {
      gstinList: payload.gstinList.join(','),
      fp: payload.filingPeriod,
      ft: payload.returnType,
      fileInputFormat: 'INVOICE',
    };

    return this.http.post<ApiResp>('gstr/upload', formData, { params }).pipe(
      map((resp) => {
        if (resp.status) return <ReturnUpload>resp.response;

        this.toastError(resp.message);
        return null;
      }),
    );
  }

  uploadFileGSTR6(payload: any, formData: FormData): Observable<ReturnUpload | null> {
    const params = {
      gstin: payload.gstin,
      fp: payload.filingPeriod,
      ft: payload.returnType,
    };

    return this.http.post<ApiResp>('gstr/upload', formData, { params }).pipe(
      map((resp) => {
        if (resp.status) return <ReturnUpload>resp.response;

        this.toastError(resp.message);
        return null;
      }),
    );
  }

  validateGSTINSession(params: { gstin: string }) {
    return this.http.get(`company/validateGstnSession`, { params }).pipe(
      map((resp: any) => {
        if (resp?.status) return resp;
        this.toastError(resp?.message);
        return null;
      }),
    );
  }

  discardDraft(params: { gstin: string; fp: string; formType: string }) {
    return this.http.delete(`gstr/discardDraft`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  uploadFile(payload: any, formData: FormData): Observable<ReturnUpload | null> {
    const params = {
      gstin: payload.gstin,
      fp: payload.filingPeriod,
      ft: payload.returnType,
      fileInputFormat: 'INVOICE',
    };

    return this.http.post<ApiResp>('gstr/upload', formData, { params }).pipe(
      map((resp) => {
        if (resp.status) return <ReturnUpload>resp.response;

        this.toastError(resp.message);
        return null;
      }),
    );
  }

  getUploadHistory(params: {
    gstin: string;
    fp: string;
    ft: string;
  }): Observable<UploadHistory[]> {
    return this.http.get('gstr/uploadhistory', { params }).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastError(resp.message);
        return null;
      }),
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fetchRejectionReasonList(id: string, gstin: string): Observable<RejectionReasonList[]> {
    return this.http.get(`gstr/uploadfilestatus?id=${id}&gstin=${gstin}`).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }
  fetchFileUrl(id: string): Observable<FileResponse> {
    return this.http.get(`gstr/uploadFile/download?uploadId=${id}`).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }

  getCalculatedGstinData(params: {
    refreshFlag: boolean;
    gstin: string;
    fp: string;
    returnType: string;
    action: string;
    fnl?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): Observable<any> {
    let url = `gstn/data?&refreshData=${params.refreshFlag}&fillingPeriod=${params.fp}&gstin=${params.gstin}&returnType=${params.returnType}&action=${params.action}`;
    if (params.fnl) {
      url += `&fnl=${params.fnl}`;
    }
    return this.http.get(url).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) {
          if (!resp.response) {
            if (
              params.returnType === 'GSTR6' &&
              resp.message ===
                "Can't send get request because some mandatory param in the request are missing."
            ) {
              this.toastError(
                'Your session with GST system has expired. To enable the comparative view of return summary, please activate the session.',
              );
            } else {
              this.toastError('GSTN Error: ' + resp.message);
            }
          }
          return resp.response;
        }
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  getReturnSummaryData(params: {
    gstin: string;
    fp: string;
    returnType: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): Observable<any> {
    return this.http
      .get(
        `gstr/returnSummary?fp=${params.fp}&gstin=${params.gstin}&ft=${params.returnType}`,
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((resp: any) => {
          if (resp.status) return resp.response;
          this.toastError(resp.message);
          return null;
        }),
      );
  }

  getFilingData(params: { gstin: string; returnType: string; fp: string }) {
    return this.http
      .get(
        `evc/file/details?gstin=${params.gstin}&returnPeriod=${params.fp}&returnType=${params.returnType}`,
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((resp: any) => {
          if (resp.status) return resp.response;
          this.toastError(resp.message);
          return null;
        }),
      );
  }
  getFileStatusData(params: { gstin: string; returnType: string; fp: string }) {
    return this.http
      .get(
        `gstn/fileStatus?gstin=${params.gstin}&filingPeriod=${params.fp}&returnType=${params.returnType}`,
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((resp: any) => {
          if (resp.status) return resp;
          this.toastError(resp.message);
          return null;
        }),
      );
  }

  getChecklistData(params: { gstin: string; returnType: string; fp: string }) {
    return this.http
      .get(
        `gstr/submit/checklist?gstin=${params.gstin}&fp=${params.fp}&ft=${params.returnType}`,
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((resp: any) => {
          if (resp.status) return resp.response;
          this.toastError(resp.message);
          return null;
        }),
        catchError((err) => {
          this.toastError(err.message);
          return of(err);
        }),
      );
  }

  sendOtp(payload: {
    gstin: string;
    pan: string;
    returnPeriod: string;
    returnType: string;
  }) {
    return this.http.post<ApiResp>('evc/sendOtp', payload).pipe(
      map((resp) => {
        if (resp.status) return resp.response;
        this.toastError(resp.message);
        return null;
      }),
    );
  }
  reSendOtp(payload: { gstin: string; evcOtpId: string | undefined }) {
    return this.http.post<ApiResp>('evc/resendOtp', payload).pipe(
      map((resp) => {
        if (resp.status) return resp.response;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  filingWithEvc(payload: FilePayload) {
    return this.http.post<ApiResp>('evc/file', payload).pipe(
      map((resp) => {
        if (resp.status) return resp;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  proceedToSign(payload: { gstin: string; filingPeriod: string; returnType: string }) {
    return this.http.post(`esign/proceedToSign`, payload).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastError(resp.message);
        return null;
      }),
    );
  }
  checkGarnetAdmin() {
    return this.http.get(`company/isGarnetAdmin`).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  getReturnStatus(params: { gstin: string; filingperiod: string; returntype: string }) {
    return this.http.get(`gstr/status`, { params }).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  getReturnFilingStatus(params: {
    gstin: string;
    filingperiod: string;
    returntype: string;
  }) {
    return this.http.get(`return/filing/status`, { params }).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  eSignReturnStatus() {
    return this.http.get(`https://localhost.truecopy.in:8099/status?format=json`).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp) return resp;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  getCertListData(data: FormData) {
    return this.http
      .post(`https://localhost.truecopy.in:8099/certlist`, data, { headers: undefined })
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((resp: any) => {
          if (resp) return resp;
          this.toastError(resp.message);
          return null;
        }),
      );
  }

  eSignWithDSC(data: FormData) {
    return this.http
      .post(`https://localhost.truecopy.in:8099/sign`, data, { headers: undefined })
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((resp: any) => {
          if (resp) return resp;
          this.toastError(resp.message);
          return null;
        }),
      );
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fileWithDSC(data: any) {
    return this.http.post(`gstn/communication/filling`, data).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastError(resp.message);
        return null;
      }),
    );
  }
  getHeaderMapping(fp?: string) {
    if (!fp) {
      return this.mainHeaderBefore2024;
    }
    const fpYear = parseInt(fp.substring(2), 10);
    if (fpYear >= 2024) {
      return this.mainHeader;
    } else {
      return this.mainHeaderBefore2024;
    }
  }
  getHeaderMappingGSTR6() {
    return this.mainHeaderGSTR6;
  }

  getSecSummaryColumnList(key?: string) {
    if (key === 'CDN') {
      return {
        invoiceCount: 'Record Count',
        totalTaxableValue: 'Total Taxable Value',
        totalTaxableAmt: 'Total Tax Amt.',
        totalIGSTAmount: 'IGST',
        totalCGSTAmount: 'CGST',
        totalSGSTAmount: 'SGST',
        totalCESSAmount: 'Cess',
      };
    } else if (key === 'CDNUR') {
      return {
        invoiceCount: 'Record Count',
        totalTaxableValue: 'Total Taxable Value',
        totalTaxableAmt: 'Total Tax Amt.',
        totalIGSTAmount: 'IGST',
        totalCESSAmount: 'Cess',
      };
    } else if (
      key === 'B2BA' ||
      key === 'EXPA' ||
      key === 'CDNRA' ||
      key === 'CDNURA' ||
      key === 'ECOMA'
    ) {
      return {
        invoiceCount: 'Record Count',
        totalActualInvoiceValue: 'Total Actual Invoice Value',
        totalActualTaxValue: 'Total Actual Taxable Value',
        totalActualTaxAmt: 'Total Actual Tax Amt',
        actIGST: 'Actual IGST',
        actCGST: 'Actual CGST',
        actSGST: 'Actual SGST',
        actCESS: 'Actual Cess',
        totalNetInvVal: 'Total Net Invoice Value',
        totalNetTaxValue: 'Total Net Taxable Value',
        totalNetTaxAmt: 'Total Net Tax Amt',
        netIGST: 'Net IGST',
        netCGST: 'Net CGST',
        netSGST: 'Net SGST',
        netCESS: 'Net Cess',
      };
    } else if (key === 'EXP') {
      return {
        invoiceCount: 'Record Count',
        totalInvValue: 'Total Invoice Value',
        totalTaxableValue: 'Total Taxable Value',
        totalTaxableAmt: 'Total Tax Amt.',
        totalIGSTAmount: 'IGST',
      };
    } else {
      return b2bSecSummaryKeys;
    }
  }

  getB2BSecSummaryIrisKeys = function (key?: string) {
    if (key === 'CDNUR') {
      return {
        invoiceCount: 'Record Count',
        totalTaxableValue: 'Total Taxable Value',
        totalTaxableAmt: 'Total Tax Amt.',
        totalIGSTAmount: 'IGST',
        totalCESSAmount: 'Cess',
      };
    } else if (key === 'EXP') {
      return {
        invoiceCount: 'Record Count',
        totalInvValue: 'Total Invoice Value',
        totalTaxableValue: 'Total Taxable Value',
        totalTaxableAmt: 'Total Tax Amt.',
        totalIGSTAmount: 'IGST',
      };
    } else if (key === 'CDN') {
      return {
        invoiceCount: 'Record Count',
        totalTaxableValue: 'Total Taxable Value',
        totalTaxableAmt: 'Total Tax Amt.',
        totalIGSTAmount: 'IGST',
        totalCGSTAmount: 'CGST',
        totalSGSTAmount: 'SGST',
        totalCESSAmount: 'Cess',
      };
    } else if (key === 'B2BA' || key === 'CDNRA' || key === 'CDNURA' || key === 'ECOMA') {
      return amendSecSummaryKeys;
    } else if (key == 'ECOM') {
      return {
        invoiceCount: 'Record Count',
        totalTaxableValue: 'Total Taxable Value',
        totalIGSTAmount: 'IGST',
        totalCGSTAmount: 'CGST',
        totalSGSTAmount: 'SGST',
        totalTaxableAmt: 'Total Tax Amt.',
        totalCESSAmount: 'Cess',
      };
    } else {
      return {
        invoiceCount: 'Record Count',
        totalInvValue: 'Total Invoice Value',
        totalTaxableValue: 'Total Taxable Value',
        totalTaxableAmt: 'Total Tax Amt.',
        totalIGSTAmount: 'IGST',
        totalCGSTAmount: 'CGST',
        totalSGSTAmount: 'SGST',
        totalCESSAmount: 'Cess',
      };
    }
  };

  getTable14Keys(key: string) {
    if (key == 'SUPECOM') {
      return table14SupEcomKeys;
    } else {
      return table15EcomKeys;
    }
  }

  getTaxPayerSummary(params: { ctin: string }) {
    return this.http.get(`ctinDetail/taxpayerSummary`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  getFinancialYears(): string[] {
    const d = new Date();
    let startYear = 2020;
    let endYear = startYear + 1;
    const tempYearList: string[] = [];
    let diffYear = d.getFullYear() - startYear;

    while (diffYear >= 0) {
      tempYearList.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
      diffYear--;
    }

    return tempYearList.reverse();
  }
  getFinancialYearsForRecon(): string[] {
    const d = new Date();
    const month = d.getMonth(); // 0-indexed (0 = January, 3 = April, etc.)
    const year = d.getFullYear();
    let currentFYStartYear = year;
    if (month < 3) {
      currentFYStartYear = year - 1;
    }

    let startYear = 2020;
    let endYear = startYear + 1;
    const tempYearList: string[] = [];
    let diffYear = currentFYStartYear - startYear;

    while (diffYear >= 0) {
      tempYearList.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
      diffYear--;
    }

    return tempYearList.reverse();
  }

  getStateText(code: string) {
    if (code) {
      return `${this.STATE_CODES[code]} (${code})`;
    }
    return '';
  }

  getNatureOfDocumentText(docNum?: number) {
    if (!docNum) return '';
    return `${(DOCUMENT_DETAILS_DESC as any)[docNum.toString()]}`;
  }

  deleteInvoice(body: {
    fp: string;
    ft: string;
    gstin: string;
    idList: string[];
    sectionType: string;
  }) {
    return this.http.delete(`invoice/deleteInvoice`, { body }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  deleteInvoiceBulk(body: any) {
    return this.http.delete(`bulk/invoice/delete`, { body }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        this.toastError(resp.message);
        return null;
      }),
    );
  }

  deleteAllInvoices(body: any) {
    return this.http.delete(`invoice/deleteAllInvoices`, { body }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        this.toastError(resp.message);
        return null;
      }),
    );
  }
  getProceedDownloadData(
    gstn: string,
    fp: string,
    downloadType: string | undefined,
    returnType: string,
  ) {
    return this.http
      .get(
        `invoice/checkDataforCsvAndPdf?gstin=${gstn}&fp=${fp}&downloadType=${downloadType}&returnType=${returnType}`,
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((resp: any) => {
          if (resp.status) return resp?.response;
          this.toastError(resp.message);
          return null;
        }),
      );
  }

  downloadSelectedEInvoices(payload: any) {
    return this.http
      .post(`mail/downloadSelectedInvoices`, payload, {
        headers: {
          'content-type': 'application/json;charset=UTF-8',
        },
      })
      .pipe(
        map((resp: any) => {
          if (resp.status) return resp;
          // this.toastError(resp.message);
          return null;
        }),
      );
  }

  getDefaultEmailBody(
    purchaserGstin: string,
    supplierGstin: string,
    downloadLink: string,
    type: string,
    ownerLink = `https://irisgst.com/irissapphire/`,
    businessName = `IRIS Sapphire`,
  ) {
    return DEFAULT_EMAIL_CONTENT_HTML.replace('#purchaserGstin', purchaserGstin)
      .replace('#supplierGstin', supplierGstin)
      .replace('#downloadLink', downloadLink)
      .replace('#ownerLink', ownerLink)
      .replace('#businessName', businessName)
      .replaceAll('#returnType', type);
  }

  getTaxPayerType(gstinList: string[]) {
    return this.http.post(`company/bulk/gstr1/getTaxpayerType`, { gstinList }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        this.toastError(resp.message);
        return null;
      }),
    );
  }
}
